import type {
  ServerActivityDiagnostics,
  ServerActivitySource,
  ServerHeartbeatReceipt,
  ServerHeartbeatWire,
} from '../../../shared/diagnostics/FreezeEventTypes';

const HEARTBEAT_RING_CAP = 32;

export interface ServerActivityRoom {
  id?: string;
  roomId?: string;
  sessionId?: string;
  onStateChange?: (callback: () => void) => void | (() => void);
  onMessage?: (
    type: 'netobs:serverHeartbeat',
    callback: (payload: Partial<ServerHeartbeatWire> | undefined) => void,
  ) => void | (() => void);
  connection?: {
    transport?: {
      events?: {
        onmessage?: ((event: unknown) => unknown) | null;
      };
    };
  };
}

export interface ServerActivityClock {
  monotonicNow(): number;
  wallNow(): number;
}

export interface ServerSilenceTrip {
  stallMs: number;
  roomId: string | null;
  sessionId: string | null;
  bindingGeneration: number;
  lastSource: ServerActivitySource | null;
}

type DiagnosticsPublisher = (snapshot: ServerActivityDiagnostics) => void;

/**
 * Owns the client-side server-activity doctrine used by FreezeSentinel.
 *
 * Keeping this state outside the React effect makes room replacement, stale
 * callbacks, re-arm behavior, and clock boundaries deterministic to test. The
 * monitor never emits gameplay events; it returns a silence trip to its owner.
 */
export class ServerActivityMonitor {
  private room: ServerActivityRoom | null = null;
  private disposers: Array<() => void> = [];
  private bindingGeneration = 0;
  private lastSource: ServerActivitySource | null = null;
  private lastActivityAt: number | null = null;
  private lastActivityMonotonicMs: number | null = null;
  private lastHeartbeatMonotonicMs: number | null = null;
  private heartbeatReceipts = 0;
  private statePatchReceipts = 0;
  private silenceEpisodes = 0;
  private silenceRearms = 0;
  private silenceArmed = true;
  private recentHeartbeats: ServerHeartbeatReceipt[] = [];
  private rawFrameSequence = 0;
  private currentRawFrame: {
    sequence: number;
    receivedAt: number;
    receivedMonotonicMs: number;
  } | null = null;

  constructor(
    private readonly clock: ServerActivityClock,
    private readonly publishDiagnostics?: DiagnosticsPublisher,
  ) {}

  bindRoom(nextRoom: ServerActivityRoom | null): boolean {
    if (nextRoom === this.room) return false;
    this.disposeBindings();
    this.room = nextRoom;
    this.bindingGeneration += 1;
    this.heartbeatReceipts = 0;
    this.statePatchReceipts = 0;
    this.silenceEpisodes = 0;
    this.silenceRearms = 0;
    this.recentHeartbeats = [];
    this.lastHeartbeatMonotonicMs = null;
    this.rawFrameSequence = 0;
    this.currentRawFrame = null;

    if (!nextRoom) {
      this.lastSource = null;
      this.lastActivityAt = null;
      this.lastActivityMonotonicMs = null;
      this.silenceArmed = true;
      this.publish();
      return true;
    }

    const boundRoom = nextRoom;
    this.bindRawFrameProbe(nextRoom);
    const stateReceipt = (): void => {
      this.recordActivity(boundRoom, 'state_patch');
    };
    const heartbeatReceipt = (payload: Partial<ServerHeartbeatWire> | undefined): void => {
      this.recordActivity(boundRoom, 'heartbeat', payload);
    };

    try {
      const dispose = nextRoom.onStateChange?.(stateReceipt);
      if (typeof dispose === 'function') this.disposers.push(dispose);
    } catch {
      // The room may still be hydrating. FreezeSentinel retries binding when
      // NetworkManager replaces the room object; diagnostics never break boot.
    }
    try {
      const dispose = nextRoom.onMessage?.('netobs:serverHeartbeat', heartbeatReceipt);
      if (typeof dispose === 'function') this.disposers.push(dispose);
    } catch {
      // Same diagnostic-only boundary as the state subscription above.
    }

    // A connected room is the initial activity boundary. The first silence
    // episode can therefore detect a room that never delivers another pulse.
    this.recordActivity(boundRoom, 'room_bind');
    return true;
  }

  checkSilence(thresholdMs: number): ServerSilenceTrip | null {
    if (!this.room || this.lastActivityMonotonicMs == null || !this.silenceArmed) return null;
    const stallMs = this.clock.monotonicNow() - this.lastActivityMonotonicMs;
    if (stallMs <= thresholdMs) return null;
    this.silenceArmed = false;
    this.silenceEpisodes += 1;
    this.publish();
    return {
      stallMs,
      roomId: this.resolveRoomId(this.room),
      sessionId: this.resolveSessionId(this.room),
      bindingGeneration: this.bindingGeneration,
      lastSource: this.lastSource,
    };
  }

  /** A hidden-tab resume and a confirmed main-thread freeze both invalidate
   * elapsed heartbeat age because callbacks could not run. */
  resetSilenceClock(): void {
    if (!this.room) return;
    this.lastActivityAt = this.clock.wallNow();
    this.lastActivityMonotonicMs = this.clock.monotonicNow();
    if (!this.silenceArmed) this.silenceRearms += 1;
    this.silenceArmed = true;
    this.publish();
  }

  getSnapshot(): ServerActivityDiagnostics {
    return {
      capturedAt: this.clock.wallNow(),
      roomId: this.resolveRoomId(this.room),
      sessionId: this.resolveSessionId(this.room),
      bindingGeneration: this.bindingGeneration,
      lastSource: this.lastSource,
      lastActivityAt: this.lastActivityAt,
      lastActivityMonotonicMs: this.lastActivityMonotonicMs,
      heartbeatReceipts: this.heartbeatReceipts,
      statePatchReceipts: this.statePatchReceipts,
      silenceEpisodes: this.silenceEpisodes,
      silenceRearms: this.silenceRearms,
      silenceArmed: this.silenceArmed,
      recentHeartbeats: this.recentHeartbeats.slice(),
    };
  }

  dispose(): void {
    this.disposeBindings();
    this.room = null;
  }

  private recordActivity(
    sourceRoom: ServerActivityRoom,
    source: ServerActivitySource,
    heartbeat?: Partial<ServerHeartbeatWire>,
  ): void {
    // Colyseus listeners may outlive a replaced room. Never let an old room
    // re-arm the active room's silence episode.
    if (sourceRoom !== this.room) return;
    const monotonicNow = this.clock.monotonicNow();
    const wallNow = this.clock.wallNow();
    this.lastSource = source;
    this.lastActivityAt = wallNow;
    this.lastActivityMonotonicMs = monotonicNow;
    if (!this.silenceArmed) this.silenceRearms += 1;
    this.silenceArmed = true;

    if (source === 'heartbeat') {
      const previousHeartbeat = this.lastHeartbeatMonotonicMs;
      this.lastHeartbeatMonotonicMs = monotonicNow;
      this.heartbeatReceipts += 1;
      this.recentHeartbeats.push({
        roomId: this.resolveRoomId(sourceRoom),
        sessionId: this.resolveSessionId(sourceRoom),
        sequence: Number.isFinite(heartbeat?.sequence) ? Number(heartbeat?.sequence) : null,
        traceId: typeof heartbeat?.__netTrace?.traceId === 'string' ? heartbeat.__netTrace.traceId : null,
        serverSequence: Number.isFinite(heartbeat?.__netTrace?.serverSeq)
          ? Number(heartbeat?.__netTrace?.serverSeq)
          : null,
        roomInstanceId: typeof heartbeat?.__netTrace?.roomInstanceId === 'string'
          ? heartbeat.__netTrace.roomInstanceId
          : null,
        serverSentAt: Number.isFinite(heartbeat?.__netTrace?.serverSentAt)
          ? Number(heartbeat?.__netTrace?.serverSentAt)
          : Number.isFinite(heartbeat?.t) ? Number(heartbeat?.t) : null,
        clientReceivedAt: wallNow,
        clientReceiptMonotonicMs: monotonicNow,
        rawFrameSequence: this.currentRawFrame?.sequence ?? null,
        rawFrameReceivedAt: this.currentRawFrame?.receivedAt ?? null,
        rawToCallbackMs: this.currentRawFrame == null
          ? null
          : monotonicNow - this.currentRawFrame.receivedMonotonicMs,
        gapMs: previousHeartbeat == null ? null : monotonicNow - previousHeartbeat,
      });
      if (this.recentHeartbeats.length > HEARTBEAT_RING_CAP) {
        this.recentHeartbeats.splice(0, this.recentHeartbeats.length - HEARTBEAT_RING_CAP);
      }
    } else if (source === 'state_patch') {
      this.statePatchReceipts += 1;
    }
    this.publish();
  }

  private publish(): void {
    this.publishDiagnostics?.(this.getSnapshot());
  }

  private bindRawFrameProbe(room: ServerActivityRoom): void {
    const events = room.connection?.transport?.events;
    const original = events?.onmessage;
    if (!events || typeof original !== 'function') return;
    const wrapped = (event: unknown): unknown => {
      this.currentRawFrame = {
        sequence: ++this.rawFrameSequence,
        receivedAt: this.clock.wallNow(),
        receivedMonotonicMs: this.clock.monotonicNow(),
      };
      try {
        return original.call(events, event);
      } finally {
        this.currentRawFrame = null;
      }
    };
    events.onmessage = wrapped;
    this.disposers.push(() => {
      if (events.onmessage === wrapped) events.onmessage = original;
    });
  }

  private disposeBindings(): void {
    for (const dispose of this.disposers.splice(0)) {
      try {
        dispose();
      } catch {
        // Diagnostic cleanup must be idempotent.
      }
    }
  }

  private resolveRoomId(room: ServerActivityRoom | null): string | null {
    if (!room) return null;
    return typeof room.roomId === 'string' ? room.roomId : typeof room.id === 'string' ? room.id : null;
  }

  private resolveSessionId(room: ServerActivityRoom | null): string | null {
    return room && typeof room.sessionId === 'string' ? room.sessionId : null;
  }
}
